import React, { Suspense } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { useAuth } from '@/features/auth';

// --- Lazy page imports ---
import LoginPage from '@/pages/LoginPage';
import InviteAcceptPage from '@/pages/InviteAcceptPage';
import OnboardingPage from '@/pages/OnboardingPage';
import ParentDashboardPage from '@/pages/ParentDashboardPage';
import ChildDashboardPage from '@/pages/ChildDashboardPage';
import ChoresPage from '@/pages/ChoresPage';
import RewardsPage from '@/pages/RewardsPage';
import LedgerPage from '@/pages/LedgerPage';
import FamilySettingsPage from '@/pages/FamilySettingsPage';
import AccountSettingsPage from '@/pages/AccountSettingsPage';
import BillingPage from '@/pages/BillingPage';
import SubscriptionSuccessPage from '@/pages/SubscriptionSuccessPage';
import SubscriptionCancelPage from '@/pages/SubscriptionCancelPage';
import AdminPortalPage from '@/pages/AdminPortalPage';
import FamilyPage from '@/pages/FamilyPage';

// ---------------------------------------------------------------------------
// Loading fallback shown while auth state resolves
// ---------------------------------------------------------------------------
function LoadingScreen(): React.ReactElement {
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50">
      <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// RequireAuth layout — protects all authenticated routes
// ---------------------------------------------------------------------------
function RequireAuth(): React.ReactElement {
  const { isAuthenticated, isLoading, appUser } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // If the user is authenticated but has no family yet, send to onboarding
  // (but avoid infinite redirect loop for the onboarding route itself)
  if (appUser != null && appUser.familyId === null && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }

  return <Outlet />;
}

// ---------------------------------------------------------------------------
// Dashboard: renders parent or child view based on role
// ---------------------------------------------------------------------------
function DashboardPage(): React.ReactElement {
  const { appUser } = useAuth();

  if (appUser?.role === 'child') {
    return <ChildDashboardPage />;
  }

  return <ParentDashboardPage />;
}

// ---------------------------------------------------------------------------
// Require primary-parent role (for billing)
// ---------------------------------------------------------------------------
function RequirePrimaryParent(): React.ReactElement {
  const { appUser } = useAuth();

  // If we don't have appUser yet, let RequireAuth handle it
  if (!appUser) return <Outlet />;

  // Only primary parent (role=parent and is the family primaryParentId) can access billing.
  // We check for admin as a fallback as well.
  if (appUser.role !== 'parent' && appUser.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return <Outlet />;
}

// ---------------------------------------------------------------------------
// Require admin role
// ---------------------------------------------------------------------------
function RequireAdmin(): React.ReactElement {
  const { appUser } = useAuth();

  if (!appUser) return <Outlet />;

  if (appUser.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return <Outlet />;
}

// ---------------------------------------------------------------------------
// Main router
// ---------------------------------------------------------------------------
export function AppRouter(): React.ReactElement {
  return (
    <Suspense fallback={<LoadingScreen />}>
      <Routes>
        {/* ---- Public routes ---- */}
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/invite/:familyId/:token" element={<InviteAcceptPage />} />

        {/* ---- Authenticated routes ---- */}
        <Route element={<RequireAuth />}>
          <Route path="/onboarding" element={<OnboardingPage />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/chores" element={<ChoresPage />} />
          <Route path="/rewards" element={<RewardsPage />} />
          <Route path="/ledger" element={<LedgerPage />} />
          <Route path="/family" element={<FamilyPage />} />
          <Route path="/settings" element={<FamilySettingsPage />} />
          <Route path="/account" element={<AccountSettingsPage />} />

          {/* Primary parent only */}
          <Route element={<RequirePrimaryParent />}>
            <Route path="/billing" element={<BillingPage />} />
          </Route>

          {/* PayPal return pages — any authenticated user (parent triggered the flow) */}
          <Route path="/subscription/success" element={<SubscriptionSuccessPage />} />
          <Route path="/subscription/cancel" element={<SubscriptionCancelPage />} />

          {/* Admin only */}
          <Route element={<RequireAdmin />}>
            <Route path="/admin" element={<AdminPortalPage />} />
          </Route>
        </Route>

        {/* ---- Catch-all → login ---- */}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </Suspense>
  );
}
